import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { EmptyState, PageHeader, StatCard } from "../../components/Common";
import { ConfirmButton } from "../../components/ConfirmButton";
import { RangeFilter } from "../../components/RangeFilter";
import { presetRange, rangeLabel, rangeParams } from "../../lib/range";
import { api, dateLabel, formatApiError, formatIDR, todayStr } from "../../lib/api";
import { money, useShowAmounts } from "../../lib/privacy";
import { EyeToggle } from "../../components/VendorFinance";

const NEW = "__new";
const selectCls = "h-11 sm:h-10 w-full rounded-md border border-input bg-white px-3 text-sm";

function VendorField({ vendors, value, initial, onChange }) {
  const [custom, setCustom] = useState(false);
  useEffect(() => { setCustom(!!initial && !vendors.includes(initial)); }, [vendors, initial]);
  return (
    <div className="space-y-1.5">
      <Label>Nama Vendor</Label>
      <select value={custom ? NEW : value} onChange={(e) => { const v = e.target.value; setCustom(v === NEW); onChange(v === NEW ? "" : v); }} className={selectCls} data-testid="vi-vendor-select">
        <option value="" disabled>Pilih vendor</option>
        {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
        <option value={NEW}>+ Vendor lain…</option>
      </select>
      {custom && <Input autoFocus placeholder="Ketik nama vendor baru" value={value} onChange={(e) => onChange(e.target.value)} data-testid="vi-vendor-new-input" />}
    </div>
  );
}

function IncomeDialog({ entry, vendors, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (entry) setForm(entry.id ? { vendor: entry.vendor, amount: entry.amount, paid_date: entry.paid_date, note: entry.note } : { vendor: "Viator", amount: "", paid_date: todayStr(), note: "" });
  }, [entry]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!form.vendor?.trim()) return toast.error("Pilih atau isi nama vendor");
    if (!Number.isInteger(amount) || amount < 1) return toast.error("Nominal harus angka bulat lebih dari 0");
    setSaving(true);
    try {
      const payload = { ...form, vendor: form.vendor.trim(), amount };
      if (entry.id) await api.put(`/vendor-income/${entry.id}`, payload);
      else await api.post("/vendor-income", payload);
      toast.success(entry.id ? "Pendapatan diperbarui" : "Pendapatan tersimpan");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="vi-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{entry?.id ? "Edit Pendapatan" : "Tambah Pendapatan"}</DialogTitle>
          <DialogDescription>Uang masuk dari vendor. Dihitung berdasarkan tanggal dibayar.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <VendorField vendors={vendors} value={form.vendor || ""} initial={entry?.vendor} onChange={set("vendor")} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nominal Total (Rp)</Label><Input type="number" inputMode="numeric" min={1} step={1} required className="font-num h-11 sm:h-10" value={form.amount ?? ""} onChange={set("amount")} data-testid="vi-amount-input" /></div>
            <div className="space-y-1.5"><Label>Tanggal Dibayar</Label><Input type="date" required className="h-11 sm:h-10" value={form.paid_date || ""} onChange={set("paid_date")} data-testid="vi-date-input" /></div>
          </div>
          {Number(form.amount) > 0 && <p className="text-xs text-slate-500 font-num" data-testid="vi-amount-preview">{formatIDR(Number(form.amount))}</p>}
          <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Textarea rows={3} value={form.note || ""} onChange={set("note")} placeholder="Contoh: Payout periode 1–15 Okt" data-testid="vi-note-input" /></div>
          <Button type="submit" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="vi-submit-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Simpan</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IncomeRow({ e, i, onEdit, onDelete }) {
  const shown = useShowAmounts();
  return (
    <div className="rise flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 border-t border-slate-100 first:border-t-0" style={{ animationDelay: `${i * 30}ms` }} data-testid={`vi-row-${e.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-violet-50 text-violet-700" data-testid={`vi-row-vendor-${e.id}`}>{e.vendor}</span>
          <span className="text-xs text-slate-500">Dibayar {dateLabel(e.paid_date)}</span>
        </div>
        {e.note && <p className="text-sm text-slate-600 mt-1.5 break-words">{e.note}</p>}
      </div>
      <p className="font-num font-bold text-lg text-slate-900" data-testid={`vi-row-amount-${e.id}`}>{money(e.amount, shown)}</p>
      <div className="flex gap-2">
        <Button variant="outline" className="h-11 sm:h-9" onClick={() => onEdit(e)} data-testid={`vi-edit-${e.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
        <ConfirmButton title="Hapus entri ini?" description={`${e.vendor} · ${money(e.amount, shown)} akan dihapus permanen.`} onConfirm={() => onDelete(e)} testId={`vi-delete-${e.id}`} confirmLabel="Hapus">
          <Button variant="ghost" className="h-11 sm:h-9 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`vi-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Hapus</Button>
        </ConfirmButton>
      </div>
    </div>
  );
}

export default function AdminVendorIncome() {
  const [range, setRange] = useState(() => presetRange("month"));
  const [vendor, setVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const shown = useShowAmounts();

  const load = () => {
    api.get("/vendor-income/vendors").then((r) => setVendors(r.data));
    api.get("/vendor-income", { params: { ...rangeParams(range), vendor: vendor || undefined } }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [range, vendor]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (e) => {
    try { await api.delete(`/vendor-income/${e.id}`); toast.success("Entri dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const total = (rows || []).reduce((s, r) => s + r.amount, 0);

  return (
    <div>
      <PageHeader eyebrow="Keuangan · Admin" title="Pendapatan Vendor">
        <EyeToggle testId="vi-eye-toggle" />
        <Button onClick={() => setEditing({})} className="h-11 sm:h-10 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="vi-add-btn"><Plus className="h-4 w-4 mr-1" />Tambah Pendapatan</Button>
      </PageHeader>
      <RangeFilter value={range} onChange={setRange} />
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 mb-6 items-end">
        <label className="space-y-1"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Vendor</span>
          <select value={vendor} onChange={(e) => setVendor(e.target.value)} className={`${selectCls} sm:w-64 block`} data-testid="vi-filter-vendor">
            <option value="">Semua Vendor</option>
            {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
      </div>
      <div className="mb-6"><StatCard tone="dark" label={`Total · ${vendor || "Semua Vendor"}`} value={money(total, shown)} hint={`${rows?.length || 0} entri · ${rangeLabel(range)}`} testId="vi-filter-total" /></div>
      {rows && (rows.length ? (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="vi-list">
          {rows.map((e, i) => <IncomeRow key={e.id} e={e} i={i} onEdit={setEditing} onDelete={remove} />)}
        </div>
      ) : (
        <EmptyState title="Belum ada pendapatan" text="Belum ada entri pada filter ini. Tekan “Tambah Pendapatan” untuk mencatat." testId="vi-empty" />
      ))}
      <IncomeDialog entry={editing} vendors={vendors} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
